import React, { useEffect, useState } from "react"
import { Link, useNavigate, useLocation } from "react-router-dom"
import {
    auth,
    logInWithEmailAndPassword,
    signInWithGoogle,
    sendEmailVerificationFromFirebase
} from "./firebase"
import "./static/Login.css"
import Background from "./static/research-login-background.png"
import googleLogo from "./static/google_logo2.png"
import { Button, CssBaseline, Box, Grid, Typography } from "@mui/material"
import { createTheme, ThemeProvider, styled } from "@mui/material/styles"
import api from "./api_ui"
import { useAuthState } from "react-firebase-hooks/auth"
import useUser from "./hooks/useUser"
import Toast from "./common/Toast"
import TermsModal from "./common/TermsModal"

const GoogleLogo = styled("img")({
    width: "100%",
    height: "100%",
    borderRadius: "50%"
})

const Copyright = (props) => {
    return (
        <Typography
            variant='body2'
            color='text.secondary'
            align='center'
            {...props}>
            {"Copyright © "}
            <Link color='inherit' href='https://finsoftai.com/'>
                FinSoftAi
            </Link>{" "}
            {new Date().getFullYear()}
            {"."}
        </Typography>
    )
}

const theme = createTheme()

function Login() {
    const [email, setEmail] = useState("")
    const [password, setPassword] = useState("")
    const [isLoggingIn, setIsLoggingIn] = useState(false)
    const [isAcceptingTerms, setIsAcceptingTerms] = useState(false)
    const {
        currentUser,
        loading,
        error: userError,
        refreshUserData
    } = useUser()
    const [user] = useAuthState(auth)
    const navigate = useNavigate()
    const location = useLocation()
    const [showTermsModal, setShowTermsModal] = useState(false)
    const [toast, setToast] = useState({
        open: false,
        message: "",
        severity: "info"
    })

    const showToast = (message, severity = "info") => {
        setToast({ open: true, message, severity })
    }

    const closeToast = () => {
        setToast({ ...toast, open: false })
    }

    // Show registration success message if coming from register
    useEffect(() => {
        if (location.state?.message) {
            showToast(location.state.message, "success")
            if (location.state?.email) {
                setEmail(location.state.email)
            }
            // Clear the state to prevent message from showing again
            navigate(location.pathname, { replace: true, state: {} })
        }
    }, [location.state, navigate])

    // Handle user data when it becomes available through useUser hook
    useEffect(() => {
        if (currentUser && !loading && !isLoggingIn) {
            // Check if user needs to accept agreement
            if (currentUser.isVerified && !currentUser.isAgreementAccept) {
                setShowTermsModal(true)
                return
            }

            // If user has accepted agreement, navigate to dashboard
            if (currentUser.isVerified && currentUser.isAgreementAccept) {
                navigate("/board", { replace: true })
            }
        }
    }, [currentUser, loading, navigate, isLoggingIn])

    const handleEmailLogin = async () => {
        // Basic validation
        if (!email.trim()) {
            showToast("Please enter your email address", "error")
            return
        }
        if (!password.trim()) {
            showToast("Please enter your password", "error")
            return
        }

        setIsLoggingIn(true)

        try {
            const result = await logInWithEmailAndPassword(email, password)

            if (result?.success) {
                if (!result.isVerified) {
                    // User is not verified, offer to resend verification email
                    showToast(
                        "Please verify your email before logging in. Check your inbox for verification email.",
                        "warning"
                    )

                    // Auto-send verification email
                    setTimeout(async () => {
                        const verifyResult =
                            await sendEmailVerificationFromFirebase()
                        if (verifyResult?.success) {
                            showToast(
                                "Verification email sent! Please check your inbox.",
                                "info"
                            )
                        }
                    }, 1000)

                    setIsLoggingIn(false)
                    return
                }

                showToast("Login successful!", "success")
                window.location.reload()
            } else {
                showToast(
                    result?.error || "Login failed. Please try again.",
                    "error"
                )
            }
        } catch (err) {
            console.error("Login error:", err)
            showToast("Login failed. Please try again.", "error")
        } finally {
            setIsLoggingIn(false)
        }
    }

    const handleAcceptTerms = async () => {
        setIsAcceptingTerms(true)

        try {
            // Get the current Firebase user for email verification status
            const firebaseUser = auth.currentUser
            const isUserVerified = firebaseUser
                ? firebaseUser.emailVerified
                : false

            // Update user with agreement acceptance and verification status
            const { success, error } = await api.update_user({
                id: currentUser.id,
                isAgreementAccept: true,
                isVerified: isUserVerified
            })

            if (success) {
                setShowTermsModal(false)
                showToast("Welcome to SST Research!", "success")

                // Force refresh user data to get the updated agreement status
                refreshUserData()

                // Navigate to dashboard after 5 seconds
                setTimeout(() => {
                    navigate("/board", { replace: true })
                }, 5000)
            } else {
                showToast(
                    error || "Failed to accept terms. Please try again.",
                    "error"
                )
            }
        } catch (err) {
            console.error("Error accepting terms:", err)
            showToast("Failed to accept terms. Please try again.", "error")
        } finally {
            setIsAcceptingTerms(false)
        }
    }

    return (
        <>
            <ThemeProvider theme={theme}>
                <Grid container component='main' sx={{ height: "100vh" }}>
                    <CssBaseline />

                    <Grid
                        justifyContent={"flex-end"}
                        item
                        xs={12}
                        sm={12}
                        md={12}
                        sx={{
                            backgroundImage: `url(${Background})`,
                            backgroundRepeat: "no-repeat",
                            backgroundColor: "black",
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            textAlign: "center"
                        }}>
                        <Box
                            className='login_box'
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "end",
                                height: "100%",
                                width: "100%",
                                backgroundColor: "transparent",
                                pr: "5%"
                            }}>
                            <Box sx={{ mt: "auto", mb: "auto" }}>
                                <div className='login__container'>
                                    <Typography
                                        variant='body1'
                                        style={{
                                            color: "#2563eb",
                                            fontWeight: 600,
                                            marginBottom: 16,
                                            textAlign: "center",
                                            lineHeight: 1.5,
                                            width: "250px",
                                            margin: "10px auto"
                                        }}>
                                        This product is optimized for desktop
                                        and laptop use only. For the best
                                        experience, please access it from a
                                        computer.
                                    </Typography>
                                    <Typography
                                        className='mb-2'
                                        component='h4'
                                        variant='h5'
                                        style={{
                                            color: "#858585",
                                            textAlign: "center"
                                        }}>
                                        Sign in
                                    </Typography>
                                    <input
                                        type='text'
                                        className='login__textBox'
                                        value={email}
                                        onChange={(e) =>
                                            setEmail(e.target.value)
                                        }
                                        placeholder='E-mail Address'
                                    />
                                    <input
                                        type='password'
                                        className='login__textBox'
                                        value={password}
                                        onChange={(e) =>
                                            setPassword(e.target.value)
                                        }
                                        placeholder='Password'
                                    />
                                    <button
                                        className='button bg-green-400'
                                        onClick={handleEmailLogin}
                                        disabled={isLoggingIn}
                                        style={{
                                            opacity: isLoggingIn ? 0.7 : 1,
                                            cursor: isLoggingIn
                                                ? "not-allowed"
                                                : "pointer"
                                        }}>
                                        {isLoggingIn
                                            ? "Signing in..."
                                            : "Login"}
                                    </button>
                                    <div>
                                        <Link to='/reset'>Forgot Password</Link>
                                    </div>

                                    <div>
                                        New User?{" "}
                                        <Link to='/register'>Sign Up Here</Link>
                                    </div>
                                    <div>
                                        <hr className='inline-hr' />
                                        <span
                                            style={{
                                                fontSize: 15,
                                                fontWeight: 300,
                                                marginTop: 10,
                                                color: "#858585"
                                            }}>
                                            or login with
                                        </span>
                                        <hr className='inline-hr' />
                                    </div>
                                    <div
                                        style={{
                                            width: "100%",
                                            textAlign: "center"
                                        }}>
                                        <Button
                                            onClick={signInWithGoogle}
                                            variant='contained'
                                            sx={{
                                                borderRadius: "50%",
                                                minWidth: 0,
                                                p: 1,
                                                m: 1,
                                                boxShadow:
                                                    "0px 1px 5px rgba(0, 0, 0, 0.2)",
                                                backgroundColor: "white",
                                                width: 50,
                                                height: 50
                                            }}>
                                            <GoogleLogo
                                                src={googleLogo}
                                                alt='Google'
                                            />
                                        </Button>
                                    </div>
                                    <Copyright sx={{ mt: 2 }} />
                                </div>
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </ThemeProvider>

            <TermsModal
                open={showTermsModal}
                onAccept={handleAcceptTerms}
                onClose={() => setShowTermsModal(false)}
                loading={isAcceptingTerms}
            />

            <Toast
                open={toast.open}
                message={toast.message}
                severity={toast.severity}
                onClose={closeToast}
            />
        </>
    )
}
export default Login
